import React, { useState, useEffect, ChangeEvent } from 'react';

interface NumericInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value: number | undefined;
  onChange: (value: number | undefined) => void;
}

const NumericInput: React.FC<NumericInputProps> = ({ value, onChange, ...rest }) => {
  const [displayValue, setDisplayValue] = useState<string>(value === undefined ? '' : String(value));

  // Update display value if the external value prop changes
  useEffect(() => {
    const propStringValue = value === undefined ? '' : String(value);

    // Prevent effect from overwriting intermediate user input like '-' or '5.'
    // Check if displayValue is exactly '-' and the incoming value is undefined.
    if (displayValue === '-' && value === undefined) {
        return; // User typed '-', parent sent undefined, keep '-' displayed.
    }
    // Check if displayValue ends with a decimal point (and not ..) and the parsed value matches the prop value.
    if (displayValue.endsWith('.') && !displayValue.endsWith('..') && parseFloat(displayValue) === value) {
        return; // User typed '.', numeric value matches prop, keep '.' displayed.
    }

    // If the string representation derived from the prop differs from the current display, update display.
    // This handles external changes or cases like '05' becoming 5, or clearing the input when value becomes undefined externally (and display isn't '-').
    if (propStringValue !== displayValue) {
        setDisplayValue(propStringValue);
    }
  }, [value]); // Effect depends only on the external value prop

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;

    // Regex to validate numeric input (allows empty, '-', decimals, negatives)
    // Allows '-', '-.', negative numbers, positive numbers, decimals
    const numericRegex = /^-?\d*\.?\d*$/;

    if (numericRegex.test(inputValue)) {
      setDisplayValue(inputValue); // Update display immediately

      if (inputValue === '' || inputValue === '-') {
        // If input is empty or just '-', the numeric value is undefined
        onChange(undefined);
      } else {
        const numericValue = parseFloat(inputValue);
        // Check if parseFloat resulted in a valid number
        if (!isNaN(numericValue)) {
            // Call onChange with the parsed number
            onChange(numericValue);
        } else {
           // Handle cases like "-." which parseFloat might return NaN for, treat as undefined
           onChange(undefined);
        }
      }
    }
    // If regex fails, do nothing - prevents invalid characters
  };

  return (
    <input
      type="text" // Use text type for better control over validation
      inputMode="decimal" // Hint for mobile keyboards
      value={displayValue}
      onChange={handleChange}
      {...rest} // Pass down other props like placeholder, className, etc.
    />
  );
};

export default NumericInput;
